import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api, type Page } from '../lib/api';
import { ErrorNotice } from '../ui/feedback';
import { DataTable, PageHead, Pager, useKeysetPage } from '../ui/table';
import { Num, Tag, Time } from '../ui/values';
import { AreaLabel, FilterSearch, useUrlFilter } from './common';
import { paths } from './paths';
import type { AdminPlanSummary } from './types';

/**
 * Alle Sortierpläne (Revision R4): je Team bzw. Spieler, Server, Dimension und Area einer. Die Mod lädt sie nach jeder
 * Berechnung hoch; jede Änderung erhöht die Revision. Zuletzt geänderte zuerst.
 */
export function SortPlansPage() {
  const page = useKeysetPage();
  const [server, setServer] = useUrlFilter('server');
  const query = { server, cursor: page.cursor, limit: page.limit };
  const list = useQuery({
    queryKey: ['admin', 'sort-plans', 'all', query],
    queryFn: () => api.get<Page<AdminPlanSummary>>('/admin/sort-plans', query),
    placeholderData: keepPreviousData,
  });

  return (
    <div className="stack">
      <PageHead title="Sortierpläne" />
      <p className="lead">
        Die Mod lädt einen Plan nach jeder Berechnung hoch, jede Änderung erhöht die Revision. Hat ein Teammitglied
        inzwischen einen neueren Plan gespeichert, übernimmt die Mod diesen (Konflikte stehen in den Protokollen).
      </p>
      <div className="filters">
        <FilterSearch label="Server, z. B. mc.example.net" value={server} onChange={setServer} />
      </div>
      {list.isError && <ErrorNotice error={list.error} onRetry={() => list.refetch()} />}
      <DataTable
        label="Sortierpläne"
        rows={list.data?.items}
        loading={list.isPending}
        rowKey={(p) => p.id}
        rowHref={(p) => paths.sortPlan(p.id)}
        empty={server ? 'Keine Sortierpläne auf diesem Server.' : 'Noch keine Sortierpläne. Die Mod lädt sie nach dem Sortieren hoch.'}
        columns={[
          {
            key: 'scope',
            header: 'Team / Spieler',
            cell: (p) => (
              <>
                {p.scope.name ?? <span className="muted">–</span>} {p.scope.type === 'player' && <Tag tone="neutral">Spieler</Tag>}
              </>
            ),
          },
          {
            key: 'area',
            header: 'Area · Dimension',
            cell: (p) => (
              <>
                <AreaLabel name={p.area} /> <span className="muted">· {p.dimension.replace(/^minecraft:/, '')}</span>
              </>
            ),
          },
          { key: 'server', header: 'Server', hide: 'md', cell: (p) => <span className="mono">{p.server}</span> },
          { key: 'rev', header: 'Revision', num: true, cell: (p) => <Num value={p.revision} /> },
          { key: 'chests', header: 'Kisten', num: true, hide: 'sm', cell: (p) => <Num value={p.chestCount} /> },
          { key: 'misplaced', header: 'falsch liegend', num: true, hide: 'md', cell: (p) => <Num value={p.misplacedStacks} /> },
          { key: 'unmet', header: 'ohne Platz', num: true, hide: 'md', cell: (p) => <Num value={p.unmetDemandStacks} /> },
          { key: 'by', header: 'zuletzt von', hide: 'sm', cell: (p) => p.updatedBy?.name ?? <span className="muted">–</span> },
          { key: 'at', header: 'aktualisiert', cell: (p) => <Time iso={p.updatedAt} /> },
        ]}
      />
      <Pager page={page} count={list.data?.items.length ?? 0} nextCursor={list.data?.nextCursor} />
    </div>
  );
}
